import React from "react";
import {
  Link,
  ListItem,
  TabPanel,
  TabPanels,
  UnorderedList,
  Tab,
  TabList,
  Tabs,
  Tbody,
  Tr,
  Box,
  Table,
  Td,
  Thead,
  Th,
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
} from "@chakra-ui/react";
import { Link as GatsbyLink } from "gatsby";

import Section from "../ui/common/Section";
import Paragraph from "../ui/common/Paragraph";

const AuthorsSection = (props) => {
  const { authorsRef, isPage, hash, categories } = props;

  const items = [
    {
      title: "Authorship",
      text: (
        <>
          <Paragraph>
            <i>microPublication Biology</i> follows the authorship guidelines of
            the International Committee of Medical Journal Editors (
            <Link href="http://www.icmje.org/recommendations/" isExternal>
              ICMJE
            </Link>
            ). All authors share responsibility for the content of the
            microPublication. Authorship should be limited to those persons that
            performed the experiment, any analysis, and or write-up. Submitting
            authors are required to assign authorship contributions using the
            Contributor Roles Taxonomy (
            <Link href="https://credit.niso.org/">CRediT</Link>) to provide
            transparency to the contributions of researchers to scholarly
            published work, to enable discoverability and to improve
            attribution, credit, and accountability.
          </Paragraph>
          <Paragraph>
            Individuals who do not satisfy authorship guidelines but
            participated materially in the work, should be included in the
            acknowledgement section.
          </Paragraph>
          <Paragraph>
            The Principal Investigator (PI) of the laboratory in which the work
            was performed may or may not be included at the discretion of the
            PI; however this PI must approve the submission of the article and
            provide the details of any funding source.
          </Paragraph>
          <Paragraph>
            Authors are required to accept the following conditions when
            submitting an article: “I/we declare to the best of my/our knowledge
            that the experiment is reproducible; that the submission has been
            approved by all authors; that the submission has been approved by
            the laboratory’s Principal Investigator, and that the results have
            not been published elsewhere.* The author(s) declare no conflict of
            interest.”
          </Paragraph>
          <Paragraph>
            *Note: We do not consider publication as an academic thesis,
            electronic preprint, or abstract as a prior publication.
          </Paragraph>
        </>
      ),
    },
    {
      title: "Publication Standards",
      text: (
        <>
          <Paragraph>
            Reported results must be original work that has not been published
            elsewhere. Experiments and or analyses must be well-described and
            appropriately interpreted. Each submission must include a complete
            description of the result with accompanying reagents, resources,
            tools, and methodologies that were used in the experiment and any
            analysis. Results need to be validated using appropriate controls
            and replicates, which themselves need to be reported in the article.
            All biological entities must adhere to standards and nomenclature
            set and maintained by the relevant community knowledgebase, as
            determined by
            <i>microPublication Biology</i>. All articles and accompanying data
            will be peer-reviewed.
          </Paragraph>
          <Paragraph>
            All articles are peer-reviewed. See{" "}
            <GatsbyLink to="/about/for-authors#peer-review-process">
              <u>our guidelines on peer-review</u>
            </GatsbyLink>{" "}
            for more information.
          </Paragraph>
        </>
      ),
    },
    {
      title: "Signing Up and Signing On",
      text: (
        <>
          <Paragraph>
            To submit an article, go to{" "}
            <Link href="https://portal.micropublication.org" isExternal>
              https://portal.micropublication.org
            </Link>
          </Paragraph>
          <Paragraph>
            First time users should click on ‘Sign up’ and provide a username,
            personal information and a password. Please store this information
            as all further communication, portal activity, including invitations
            to review and account resetting will require these details.
          </Paragraph>
          <Paragraph>
            Before proceeding, look in your email inbox for a verification
            message from our system, if you do not receive one within a few
            minutes, there could be something wrong with your entered email
            address or your institution could be blocking emails from our mail
            server.
          </Paragraph>
          <Paragraph>
            Please let us know if you need help to set up or log into your
            account:{" "}
            <Link href="mailto:contact@micropublication.org">
              contact@micropublication.org
            </Link>
            .
          </Paragraph>
        </>
      ),
    },
    {
      title: "Starting a microPublication (the Metadata)",
      text: (
        <Tabs variant="soft-rounded" colorScheme="teal" size="sm">
          <TabList>
            <Tab>Authorship and contributions</Tab>
            <Tab>Apply Categories to your article</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <Paragraph>
                <b>Name</b> - Please add names in the order of authorship. You
                are required to appoint both a <b>Submitting Author</b>, who
                will be the main contact during the submission process and a{" "}
                <b>Corresponding Author</b> who will be the main contact for the
                article after publication. The Submitting and Corresponding
                Authors can be the same person.
              </Paragraph>
              <Paragraph>
                <b>Affiliation</b> - Please include the institute name, city,
                state/region and country for all authors. Do not include street
                addresses or mail-codes.
              </Paragraph>
              <Paragraph>
                <b>ORCID</b> - include ORCID iDs for all authors.
              </Paragraph>
              <Paragraph>
                <b>CRediT</b> - Contributor Roles Taxonomy. You are required to
                describe each authors’ contribution to the work that is being
                submitted, using the CRediT taxonomy. See the{" "}
                <Link href="https://credit.niso.org/" isExternal>
                  NISO website
                </Link>{" "}
                for more information.
              </Paragraph>
              <Paragraph>
                For long author's lists, authors can fill out a spreadsheet. You
                can contact{" "}
                <Link href="mailto:editors@micropublication.org">
                  editors@micropublication.org
                </Link>{" "}
                for more information.
              </Paragraph>
            </TabPanel>
            <TabPanel>
              <Paragraph>
                Articles in <i>microPublication Biology</i> are categorized by
                Species and Data Type (e.g., Expression Data, Phenotype Data,
                New Methods, Software, etc.). In addition, articles are labeled
                by the Nature of the Findings (New Finding, Negative Result,
                Replication -Successful, etc). These categories and labels will
                be used to
              </Paragraph>
              <UnorderedList marginLeft="50px">
                <ListItem>
                  inform editorial staff of the type of data being reported
                  guide
                </ListItem>
                <ListItem>
                  assignment of the article to the appropriate curators at
                </ListItem>
                <ListItem>
                  collaborating databases ease searching and indexing on our
                  journal site
                </ListItem>
              </UnorderedList>
              <Paragraph>
                These categories and labels will continue to expand as needed.
              </Paragraph>
              <UnorderedList marginLeft="50px">
                <ListItem>
                  <b>Species</b>
                  <Paragraph>
                    Choose a species from the dropdown, if your species is not
                    listed there, please select ‘Other’.
                  </Paragraph>
                </ListItem>
                <ListItem>
                  <b>Data Type</b>
                  <Paragraph>
                    One of the goals of microPublication is to integrate
                    research findings into Knowledgebases for bioinformatic
                    indexing and curation. To facilitate such integration, when
                    possible, we guide authors by providing data type specific
                    curation forms. If the form for a specific data type is not
                    available, we will alert a Curator at the relevant
                    knowledgebase. Data types currently covered are:
                  </Paragraph>
                  {categories &&
                    categories.map((category) => (
                      <Box key={category.value} margin="2em">
                        {category.label}: {category.description}
                      </Box>
                    ))}
                </ListItem>
                <ListItem>
                  <b>Nature of microPublication Findings</b>. These speak to the
                  kind of result you are reporting and are as follows:
                  <Paragraph>
                    <i>New finding</i> -a first-time published discovery or
                    result, including first-time published new reagents
                  </Paragraph>
                  <Box margin="2em">
                    Example articles:{" "}
                    <Link
                      href="https://doi.org/10.17912/micropub.biology.000188"
                      isExternal
                    >
                      Ratemi, Kiss, and Rocheleau, 2021
                    </Link>
                    ;{" "}
                    <Link href="https://doi.org/10.17912/W2XD3R" isExternal>
                      Abay et al., 2017
                    </Link>
                    ;{" "}
                    <Link
                      href="https://doi.org/10.17912/micropub.biology.000303"
                      isExternal
                    >
                      Kazatskaya et al., 2020
                    </Link>
                  </Box>
                  <Paragraph>
                    <i>Finding not previously shown</i> -an unpublished result
                    mentioned in a prior publication, but never included, these
                    include data not shown.
                  </Paragraph>
                  <Box margin="2em">
                    Example article:{" "}
                    <Link href="https://doi.org/10.17912/W27P4P" isExternal>
                      Michael, 2017
                    </Link>
                  </Box>
                  <Paragraph>
                    <i>Negative result</i> -a result from a solid experiment
                    that did not produce a result you expected and so does not
                    support your hypothesis. Such results are often useful, but
                    left out of scholarly communication.
                  </Paragraph>
                  <Box margin="2em">
                    Example articles:{" "}
                    <Link
                      href="https://doi.org/10.17912/micropub.biology.000307"
                      isExternal
                    >
                      McDiarmid et al., 2020
                    </Link>
                    ;{" "}
                    <Link
                      href="https://doi.org/10.17912/micropub.biology.000365"
                      isExternal
                    >
                      Tembo et al., 2021
                    </Link>
                    ;{" "}
                    <Link
                      href="https://doi.org/10.17912/micropub.biology.000144"
                      isExternal
                    >
                      Coleman-Hulbert et al., 2019
                    </Link>
                  </Box>
                  <Paragraph>
                    <i>Replication – successful</i> -a report that supports a
                    previous outcome of a similar experiment.
                  </Paragraph>
                  <Box margin="2em">
                    Example articles:{" "}
                    <Link href="https://doi.org/10.17912/sg7d-sd61" isExternal>
                      Benner and Oliver, 2018
                    </Link>
                    ;{" "}
                    <Link href="https://doi.org/10.17912/micropub.biology.000143">
                      Uebel and Phillips, 2019
                    </Link>
                    ;{" "}
                    <Link href="https://doi.org/10.17912/W2CC7Z" isExternal>
                      Hiebert et al., 2017
                    </Link>
                  </Box>
                  <Paragraph>
                    <i>Replication – unsuccessful</i> -a report that does not
                    support a previous outcome of a similar experiment.
                  </Paragraph>
                  <Paragraph>
                    <i>Commodity validation</i> -results reported for a
                    commercial reagent that replicate results achieved using
                    commonly available reagents and so do not require the
                    purchase of the commercial product.
                  </Paragraph>
                  <Box margin="2em">
                    Example articles:{" "}
                    <Link href="https://doi.org/10.17912/W2RP4B">
                      Robinson and McCormick, 2016
                    </Link>
                    ;{" "}
                    <Link href="https://doi.org/10.17912/W2NP4D" isExternal>
                      Brock et al., 2017
                    </Link>
                    ;{" "}
                    <Link href="https://doi.org/10.17912/W2WC7M" isExternal>
                      Weeks et al., 2016
                    </Link>
                  </Box>
                </ListItem>
              </UnorderedList>
            </TabPanel>
          </TabPanels>
        </Tabs>
      ),
    },
    {
      title: "Writing a microPublication",
      text: (
        <Accordion allowToggle>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Abstract
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  Abstracts are required by PubMed for proper display on their
                  site and for searches. Please include specific terms and
                  concepts that will help your article be found by other
                  scientists. Abstracts should not exceed 100 words in length.
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Figure
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <Paragraph>
                    microPublications are meant to be reports of single
                    experiments. We expect these data to fit within one figure.
                    Multiple panels are allowed.
                  </Paragraph>
                  <Paragraph>
                    We accept images in .jpg and .png formats with a minimum 500
                    DPI. The image should have approximately a 4:3 aspect ratio.
                  </Paragraph>
                  <Paragraph>Within your image please ensure:</Paragraph>
                  <UnorderedList marginLeft="50px">
                    <ListItem>
                      It has not been published before (exceptions will be made
                      for{" "}
                      <GatsbyLink to="/about/for-authors#integration-articles">
                        <u>Integration Articles</u>
                      </GatsbyLink>
                      )
                    </ListItem>
                    <ListItem>Solid lines are not broken up</ListItem>
                    <ListItem>Image areas are not pixilated</ListItem>
                    <ListItem>Text is legible and of high quality </ListItem>
                  </UnorderedList>
                  <Paragraph>
                    <b>
                      The text in the figure needs to be well readable at 100%
                      magnification of the PDF view.
                    </b>{" "}
                    Your article cannot be published if your figure does not
                    meet these requirements.
                  </Paragraph>
                  <Paragraph>
                    To avoid inappropriate image manipulation, see{" "}
                    <Link
                      href="http://jcb.rupress.org/content/166/1/11"
                      isExternal
                    >
                      Rossner and Yamada 2004, Journal of Cell Biology, 166:11
                    </Link>
                    .
                  </Paragraph>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Title
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  Your title should reflect the results of your experiment.
                  Please include species-specific formatting when necessary.
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Description
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  Provide background information and the question to be
                  addressed. Describe the experimental approach, the results
                  obtained, and the conclusions drawn, including caveats and
                  weaknesses where relevant. Given the brevity of a
                  microPublication, we do not employ section names such as
                  introduction, results and discussion.
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Methods
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <Paragraph>
                    Sufficient information must be provided to allow another
                    group to repeat the experimental analysis.
                  </Paragraph>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Regeants
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <Paragraph>
                    Include all pertinent reagents used and generated in your
                    study. Biological reagents such as strains, animals,
                    antibodies, and molecular tools (e.g. transgenes, plasmids)
                    should be listed in a table. For each reagent include enough
                    information for unambiguous identification, see examples
                    below.
                  </Paragraph>
                  <Paragraph>
                    For strains and plasmids, minimally provide the name, full
                    genotype, and source where it is available. Provide the full
                    genotypes for any transgene or engineered allele within the
                    strain genotype or in a separate column. You are welcome to
                    include any other relevant information in subsequent columns
                    as you wish.
                  </Paragraph>
                  <Box borderWidth="1px" borderRadius="lg" margin="5px">
                    <Table variant="striped">
                      <Thead>
                        <Tr>
                          <Th>Strain</Th>
                          <Th>Genotype</Th>
                          <Th>Available from</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>N2</Td>
                          <Td>Caenorhabditis elegans </Td>
                          <Td>CGC</Td>
                        </Tr>

                        <Tr>
                          <Td>PHX2811</Td>
                          <Td>ceh-84(syb2811[crh-84::GFP])</Td>
                          <Td>Sunybiotech</Td>
                        </Tr>
                        <Tr>
                          <Td>EG4788</Td>
                          <Td>Caenorhabditis portoensis</Td>
                          <Td>CGC</Td>
                        </Tr>
                        <Tr>
                          <Td>OH12747</Td>
                          <Td>Caenorhabditis portoensis</Td>
                          <Td>CGC</Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>
                  <Box borderWidth="1px" borderRadius="lg" margin="5px">
                    <Table variant="striped">
                      <Thead>
                        <Tr>
                          <Th>Plasmid</Th>
                          <Th>Genotype</Th>
                          <Th>Description</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>pAB1</Td>
                          <Td>
                            inx-6p(2TAAT-deletion)::tagRFP::unc-54 3’ UTR;
                          </Td>
                          <Td>
                            1654 bp region upstream of inx-6 coding region,
                            where two TAAT sites were deleted. Available at
                            Addgene.
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>
                  <Box borderWidth="1px" borderRadius="lg" margin="5px">
                    <Table variant="striped">
                      <Thead>
                        <Tr>
                          <Th>Antibody</Th>
                          <Th>Animal and clonality</Th>
                          <Th>Description</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>anti-FLD-1</Td>
                          <Td>Rabbit polyclonal</Td>
                          <Td>
                            Rabbit antiserum was generated by immunizing a
                            rabbit against the peptide TQVGDVESGPLRTQ
                            corresponding to C terminal amino acids.
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      References
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <Paragraph>
                    References can be entered one at a time or uploaded in bulk
                    from a file. Supported file formats are NBIB or RIS.
                  </Paragraph>
                  <UnorderedList>
                    <ListItem>
                      <b>Bulk import</b>
                      <Paragraph>
                        microPublication is working on streamlining reference
                        imports from commonly used reference management
                        software, such as EndNote, Zenodo, Mendeley.
                      </Paragraph>
                      <Paragraph>
                        You can create the list of references in Pubmed (export
                        the file in NBIB) or use your citation manager (export
                        the file in RIS format). In the citation manager you can
                        select any journal style before exporting the file in
                        RIS. You can then drag and drop the file in the
                        Reference section in the microPublication submission
                        form.
                      </Paragraph>
                    </ListItem>
                    <ListItem>
                      <b>Manual input</b>
                      <Paragraph>
                        Authors should provide the PubMed ID (PMID) in the
                        submission form when available. If no PMID is available,
                        a Digital Object Identifier (DOI) should be entered.
                        Exceptions will be made when needed. The PMID and DOI
                        will be added and hyperlinked to the reference citation
                        during publication processing.
                      </Paragraph>
                      <Paragraph>
                        References should be arranged alphabetically and
                        formatted following the Council of Scientific Editors
                        (CSE){" "}
                        <Link
                          href="https://www.scientificstyleandformat.org/Tools/SSF-Citation-Quick-Guide.html"
                          isExternal
                        >
                          guidelines
                        </Link>
                        .
                      </Paragraph>
                    </ListItem>
                    <ListItem>
                      <b>For references that do not have a PMID or DOI</b>
                      <Paragraph>
                        Use the following format and enter one reference per
                        box. Click on 'Add reference' to insert additional
                        references: Author(s). Date. Article title. Journal
                        title. Volume(issue):location.
                      </Paragraph>
                      <Paragraph>
                        For Dissertations and Theses we follow the
                        recommendations of the{" "}
                        <Link
                          href="https://www.scientificstyleandformat.org/Tools/SSF-Citation-Quick-Guide.html"
                          isExternal
                        >
                          CSE Manual for Authors, Editors, and Publishers
                        </Link>
                        .
                      </Paragraph>
                      <Box margin="2em">
                        Example: Lutz M. 1989. 1903: American nervousness and
                        the economy of cultural change [dissertation]. [Stanford
                        (CA)]: Stanford University. (Lutz 1989)
                      </Box>
                    </ListItem>
                    <ListItem>
                      <b>If there are more than 8 authors</b>
                      <Paragraph>
                        Display the first 6 authors, then an ellipsis or the
                        words "et al.", followed by the last author, as shown in
                        the following citation:
                      </Paragraph>
                      <Paragraph>
                        Vrailas-Mortimer AD, Aggarwal N, Ahmed NN, Alberts IM,
                        Alhawasli M, Aljerdi IA, et al., Kagey JD. 2021. B.2.16
                        is a non-lethal modifier of the Dark82 mosaic eye
                        phenotype in Drosophila melanogaster. microPublication
                        Biology. 10.17912/micropub.biology.000359.
                      </Paragraph>
                      <Paragraph>OR</Paragraph>
                      <Paragraph>
                        Vrailas-Mortimer AD, Aggarwal N, Ahmed NN, Alberts IM,
                        Alhawasli M, Aljerdi IA, ..., Kagey JD. 2021. B.2.16 is
                        a non-lethal modifier of the Dark82 mosaic eye phenotype
                        in Drosophila melanogaster. microPublication Biology.
                        10.17912/micropub.biology.000359.
                      </Paragraph>
                    </ListItem>
                    <ListItem>
                      <b>In-Text Citations</b>
                      <Paragraph>
                        Please use the APA citation style, i.e. (Author's last
                        name, Year of publication) and list your references
                        alphabetically in the Bibliography. We do not accept
                        numbered citations and references. For citations with
                        two authors, ​include both authors' names.
                      </Paragraph>
                      <Box margin="2em">
                        Example: (Ricardo and Lehmann 1999)
                      </Box>
                      <Paragraph>
                        For citations with three or more authors, list only the
                        first author's name followed by et al.
                      </Paragraph>
                      <Paragraph>
                        For citations with three or more authors, list only the
                        first author's name followed by et al.
                      </Paragraph>
                      <Box margin="2em">Example: (Kim et al., 1999)</Box>
                      <Paragraph>Cite references chronologically.</Paragraph>
                      <Box margin="2em">
                        Example: (Chen et al., 1997; Scott and Rogers 1998;
                        Isaacson 1999)
                      </Box>
                      <Paragraph>
                        For multiple citations with the same first author, list
                        single-author entries by year using 1996a, 1996b, etc.
                      </Paragraph>
                      <Box margin="2em">Example: (Chen et al., 1996a)</Box>
                    </ListItem>
                  </UnorderedList>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Statistical analysis
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <UnorderedList>
                    <ListItem>
                      Any statistical analysis that was performed must be
                      indicated, including multiple comparison corrections.
                      Standardized nomenclature for reporting statistical
                      analyses, e.g., SI units, must be followed.
                    </ListItem>
                  </UnorderedList>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      Extended data
                    </Box>
                    {isExpanded ? <Link>less...</Link> : <Link>more...</Link>}
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <Paragraph>
                    microPublication discourages the inclusion of supplemental
                    data. However, we allow authors to include ‘Extended data’
                    files which are necessary to support experimental results
                    presented in the study. Examples of acceptable Extended Data
                    are software, movies, mass spectrometry hits for proteomic
                    analysis, GFF, FASTA, Variant Call Format (VCF) files, among
                    others.
                  </Paragraph>
                  <Paragraph>
                    Extended data files should be accompanied by a descriptive
                    title and a brief description. Files will be deposited as is
                    on the{" "}
                    <Link href="https://data.caltech.edu" isExternal>
                      CaltechData
                    </Link>{" "}
                    server for long term preservation and will be hyperlinked to
                    the final version of the article. If extended data are
                    images or tables of experimental results and are exceeding
                    the scope of a single microPublication, we encourage the
                    authors to submit a separate microPublication. Extended Data
                    files will be peer-reviewed along with the article and may
                    be declined at the editor’s discretion to be included if not
                    deemed necessary for the article.
                  </Paragraph>
                  <Box margin="2em">
                    <b>Movies</b>: The acceptable format is MP4/MPEG up to 1GB
                    in size.
                  </Box>
                  <Box margin="2em">
                    <b>Software</b>: Software written, or adapted, and used by
                    the authors in the manuscript is considered data that must
                    be archived along with the article itself. If the software
                    code is not included within the article, authors should
                    include a link to a released and tagged version of the code.
                    For examples of how to release and tag software in a github
                    repository go to{" "}
                    <Link
                      href="https://docs.github.com/en/rest/releases"
                      isExternal
                    >
                      https://docs.github.com/en/rest/releases
                    </Link>
                    . Authors should provide a license for the software. A list
                    of software licenses can be found at{" "}
                    <Link href="https://opensource.org/licenses" isExternal>
                      https://opensource.org/licenses
                    </Link>
                    . The selection guide at{" "}
                    <Link href="https://choosealicense.com">
                      https://choosealicense.com
                    </Link>{" "}
                    may also be helpful in determining which license to use. The
                    software release should be compliant with institutional
                    sharing policies.
                  </Box>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
        </Accordion>
      ),
    },
    {
      title: "Adhering to Nomenclature Standards",
      text: (
        <>
          <Paragraph>
            <i>microPublication Biology</i> editors work directly with curators
            at authoritative community knowledgebases (e.g. WormBase, FlyBase,
            Saccharomyces Genome Database, etc.) to validate biological entities
            reported in submitted articles. Authors must adhere to community
            standards in reporting these reagents, and bioentity names such as
            gene, strain, variation names etc. Reported reagents and names of
            bioentities will be quality controlled for accuracy by a Curator at
            a collaborating knowledgebase or by automated methods set up by
            microPublication in conjunction with the knowledgebase to ensure
            that these objects adhere to these standards.
          </Paragraph>
          <Paragraph>
            Accepted nomenclature sources for human and major biomedical model
            organisms can be found{" "}
            <GatsbyLink to="/genetic-nomenclature">
              <u>here</u>
            </GatsbyLink>
            .
          </Paragraph>
        </>
      ),
    },
    {
      title: "Structural Biology Data",
      text: (
        <>
          <Paragraph>
            <i>microPublication Biology</i> is now partnering with{" "}
            <Link href="https://proteopedia.org/" isExternal>
              Proteopedia
            </Link>
            , a 3D web resource with pages that describe protein and nucleic
            acid structures and their structure/function relationships through
            descriptive text linked to rotatable, zoomable 3D structures. You
            can access an article with the embedded Proteopedia Molecular Tour{" "}
            <Link
              href="https://doi.org/10.17912/micropub.biology.000570"
              isExternal
            >
              here
            </Link>
            .
          </Paragraph>
          <Paragraph>
            Authors that report new macromolecular structures have now the
            opportunity to opt in and create 3D structures with models built
            from PDB data. This page is linked from the published article and
            accessible both through the journal and through the Proteopedia
            website. microPublication authors will be able to opt in during the
            submission of the manuscript. Authors will be contacted by
            Proteopedia personnel and will be asked to provide additional data
            files to build the Interactive 3D Complement (I3DC). More
            information{" "}
            <Link href="https://proteopedia.org/jrnld/" isExternal>
              here
            </Link>
            .
          </Paragraph>
        </>
      ),
    },
    {
      title: "Material Sharing",
      text: (
        <Paragraph>
          Authors are expected to make biological materials described in their
          article available upon reasonable request from researchers. Authors
          are encouraged to deposit biological materials to public repositories
          such as{" "}
          <Link href="https://www.addgene.org/" isExternal>
            Addgene
          </Link>
          ,{" "}
          <Link href="https://bdsc.indiana.edu/" isExternal>
            Bloomington Drosophila Stock Center
          </Link>
          ,{" "}
          <Link href="https://cgc.umn.edu/" isExternal>
            Caenorhabditis Genetics Center
          </Link>
          ,{" "}
          <Link href="https://xenopusresource.org/" isExternal>
            European Xenopus Resource Centre
          </Link>{" "}
          and other appropriate repositories.
        </Paragraph>
      ),
    },
    {
      title: "Artificial Intelligence",
      text: (
        <Paragraph>
          Artificial Intelligence: microPublication follows the{" "}
          <Link
            href="https://www.csescienceeditor.org/article/cse-guidance-on-machine-learning-and-artificial-intelligence-tools/"
            isExternal
          >
            CSE
          </Link>{" "}
          guidelines on the use of Artificial Intelligence, such as ChatGPT,
          Chatbots, Large Language Models (LLM) in the preparation and or
          revision of work to our journal. Authors should disclose, upon
          submission of the original and any revised version of the article, if
          they used AI or other machine learning tools in generating any text or
          images in the manuscript, and where it was used. Contributions by
          artificial intelligence (AI) tools and technologies to a study or to
          an article's contents must be clearly reported in a dedicated section
          of the Methods. We require the disclosure of the technical
          specifications (name, version, model) of the AI tool or model and how
          its application was used (query structure, syntax). We ultimately hold
          human authors accountable for all aspects of their manuscript, these
          aspects include the accuracy of content created with AI assistance,
          the absence of plagiarism, appropriate attributions of such sources.
        </Paragraph>
      ),
    },
    {
      title: "Article Submission Agreement",
      text: (
        <>
          <Paragraph>
            By submitting an article to <i>microPublication Biology</i>, all
            authors agree to the following:
          </Paragraph>
          <UnorderedList marginLeft="50px">
            <ListItem>
              All curatable data in the article will be shared with relevant and
              appropriate databases collaborating with microPublication
              journals.
            </ListItem>
            <ListItem>
              All experiments discussed in the article are reproducible.
            </ListItem>
            <ListItem>The results have not been published elsewhere.*</ListItem>
          </UnorderedList>
          <Paragraph>Further, the following must be true:</Paragraph>
          <UnorderedList marginLeft="50px">
            <ListItem>
              The submission has been approved by all authors.
            </ListItem>
            <ListItem>
              The submission has been approved by the laboratory’s Principal
              Investigator.
            </ListItem>
            <ListItem>
              There is no conflict of interest for any author.
            </ListItem>
            <ListItem>
              Contributions by artificial intelligence (AI) tools and
              technologies to this study have been described in the Methods
            </ListItem>
          </UnorderedList>
          <Paragraph>
            <i>
              *We do not consider publication as an academic thesis, electronic
              preprint, or abstract as a prior publication.
            </i>
          </Paragraph>
        </>
      ),
    },
    {
      title: "Integration Articles",
      text: (
        <Paragraph>
          If the amount of your data does not fit within one microPublication,
          we encourage you to prepare multiple microPublications, each focused
          on an independent result, and to tie the articles together with an
          Integrations article, which serves as a summary narrative article.
          Each Integrations article furthers the discussion of data reported in
          multiple microPublications. Integrations can include a final
          experiment that ties published microPublication data into a cohesive
          report. Example articles:{" "}
          <Link href="https://doi.org/10.17912/W2NW9C" isExternal>
            Abay, Wong, and Neumann, 2017
          </Link>
          ;{" "}
          <Link
            href="https://doi.org/10.17912/micropub.biology.000155"
            isExternal
          >
            Minor and Sternberg, 2019
          </Link>
          ;{" "}
          <Link href="https://doi.org/10.17912/micropub.biology.000102">
            Ashley and Holgado, 2019
          </Link>
        </Paragraph>
      ),
    },
    {
      title: "After Submission",
      text: (
        <Paragraph>
          Once an article has been submitted, a managing editor and Science
          Officer will do a quick evaluation of the article for moving on to
          peer-review. If the article passes this initial evaluation, the
          article will be sent to a reviewer. During any of these reviews, the
          authors may be asked to address comments and revise the article. To
          speed this process, authors should ​address all comments and questions
          and list/explain all changes made to the manuscript to simplify
          evaluation by the editors and reviewers. Instructions are given at the
          time of revision requests. Editorial staff and Science Officers
          reserve the right to decline or reject an article, as well as request
          corrections to the article at any point during the submission and
          proofing process.
        </Paragraph>
      ),
    },
    {
      title: "Peer Review Process",
      text: (
        <>
          <Paragraph>
            Each manuscript sent to microPublication Biology undergoes two
            rounds of peer review, first <strong>Academic Peer Review</strong>{" "}
            and then <strong>Curatorial Peer Review</strong>. An article needs
            to pass{" "}
            <strong>
              <em>both</em>
            </strong>{" "}
            academic and curatorial peer review in order to be accepted for
            publication.
          </Paragraph>
          <Paragraph>
            <em>Academic Peer Review</em>
          </Paragraph>
          <Paragraph>
            <i>microPublication Biology</i> follows the International Committee
            of Medical Journal Editors (ICMJE){" "}
            <Link href="http://www.icmje.org/urm_main.html" isExternal>
              guidlines
            </Link>{" "}
            in the ‘Recommendations for the Conduct, Reporting, Editing and
            Publication of Scholarly Work in Medical Journals’.
          </Paragraph>
          <Paragraph>
            After submission, the manuscript is sent to one or two reviewer
            experts in the field depending on the complexity of the article. The
            complexity and need for more than one reviewer will be at the
            discretion of the science editor. Authors are encouraged to suggest
            reviewers, which are evaluated by the science editor for
            appropriateness. The science editor will ultimately select the
            reviewer(s).
          </Paragraph>
          <Paragraph>
            <i>microPublication Biology</i> operates an elective open peer
            review system, where the reviewers’ names are included on final
            article html and PDF versions of the article upon publication of the
            manuscript, if the reviewer chooses open acknowledgement. During the
            review process itself the author is unaware of the identity of the
            reviewer; however, the reviewer is aware of the author’s name.
          </Paragraph>
          <Paragraph>
            Reviewers are asked to declare any conflict of interest if they
            accept the invitation to review an article and are encouraged to
            decline if they perceive a potential conflict, in which case they
            will be excluded from the review process for that article.
          </Paragraph>
          <Paragraph>
            <i>microPublication Biology</i> editors rely on the reviewer’s
            feedback, author’s compliance with suggested editorial and reviewer
            revisions, and ultimately on the science editor’s decision for
            acceptance or rejection of an article. We do not guarantee
            acceptance of submitted work. Further, while microPublication
            Biology aims at a quick turnaround for article peer review, we
            cannot guarantee a short review or processing time.
          </Paragraph>
          <Paragraph>
            <em>Curatorial Peer Review</em>
          </Paragraph>
          <Paragraph>
            microPublication journals collaborate with community knowledgebases
            to validate and vet reported data and regents so that nomenclature
            and data reporting meet community standards. A Curator at a relevant
            database, chosen by the Editors, will be enlisted to vet the data
            being reported. Authors are expected to address any feedback
            provided by the Curator as they would for Reviewer feedback.
          </Paragraph>
        </>
      ),
    },
    {
      title: "Peer Review Panel for Multiple Submission Projects",
      text: (
        <Paragraph>
          Multiple Submission Projects output regular reports that are amenable
          to a formulaic article template, such as consortia generated data or
          undergraduate CURE authored reports. For such formulaic submissions,
          we instantiate review panels composed of researchers and database
          curators. Invited researchers evaluate the reported results for being
          complete and technically sound. Database curators ensure the reported
          data meet community standards. These panels ensure consistency in
          peer-evaluation across all articles. Review panels are assembled based
          on the needs of the project, e.g. the number of submissions and the
          complexity of the articles. If you think your project will result in
          multiple output of similar articles, please contact us at{" "}
          <Link href="mailto:editors@micropublication.org">
            editors@micropublication.org
          </Link>
          .
        </Paragraph>
      ),
    },
    {
      title: "Following Acceptance - the Proof Stage",
      text: (
        <Paragraph>
          Articles go through an author-editable proof stage. While
          microPublication does not employ a copy-editor, microPublication
          Editors may request authors to address stylistic, grammatical, and or
          nonscientific issues of the article. Ultimately, authors are
          responsible for carefully proofreading their article with the goal of
          identifying grammatical or stylistic errors. Authors can make changes
          in the portal. Changes to the proof will be checked by an editor and
          any corrections that alter data, results, or experiment conclusions
          will be subject to further review.
        </Paragraph>
      ),
    },
    {
      title: "Following Publication",
      text: (
        <Paragraph>
          Articles will be available on the <i>microPublication Biology</i>{" "}
          website as soon as possible after final proof approval. We will send
          authors an announcement when their article has been published and
          available on our live site. Two weeks after going live, the article
          will be uploaded to PubMedCentral. Authors should check the
          microPublication Biology online version, both the html and PDF, for
          possible errors in assembly or content, see "Corrections After
          Publication" below.
        </Paragraph>
      ),
    },
    {
      title: "Corrections After Publication",
      text: (
        <>
          <Paragraph>
            <i>microPublication Biology</i> is amenable to fixing unintentional
            mistakes (e.g., incorrect author list, incorrect strain name, etc.)
            as we do not consider such mistakes to be misconduct. If these
            mistakes are caught within two weeks of publication, no corrigendum
            is necessary. However, after two weeks from publication, the article
            will be considered the final version for indexing and any further
            correction will require a Corrigendum (or Erratum if the error was
            an editorial oversight). If errors are found, authors are instructed
            to contact us at{" "}
            <Link href="mailto:editors@micropublication.org">
              editors@micropublication.org. We will convey all corrections to
              our indexing services (e.g. PubMed Central).
            </Link>
          </Paragraph>
          <Paragraph>
            Data handling
            <br /> Once the final version of the article for indexing has been
            established, we will send curated data to the relevant authoritative
            database and hyperlink known biological entities to that database.
            Published data will appear on the database website based on the
            database’s release cycle. If you do not see your data in the
            expected database after a couple releases, please contact that
            database.
          </Paragraph>
          <Paragraph>
            As we are an online journal, we do not provide hard-copy versions of
            articles.
          </Paragraph>
        </>
      ),
    },
    {
      title: "Copyright and Licensing",
      text: (
        <>
          <Paragraph>
            Authors retain copyright for all submitted content published in
            articles by <i>microPublication Biology</i> under the terms of the
            Creative Commons Attribution 4.0 International (
            <Link
              href="https://creativecommons.org/licenses/by/4.0/"
              isExternal
            >
              CC BY 4.0
            </Link>
            ) license, which permits unrestricted use, distribution, and
            reproduction in any medium, provided the original author(s) and
            source are credited.
          </Paragraph>
          <Paragraph>
            ​For all accepted submissions, authors agree to their data being
            delivered for integration into Model Organism Databases (or other
            authoritative repositories) by <i>microPublication Biology</i>. Each
            of these repositories contain their own copyright and licensing
            agreements. See each repository for more information.
          </Paragraph>
          ​
          <Paragraph>
            Rejected articles and data there-in will not be stored by
            <i>microPublication Biology</i>, nor will they be deposited in
            collaborating repositories.
          </Paragraph>
        </>
      ),
    },
    {
      title: "Scientific Misconduct",
      text: (
        <>
          <Paragraph>
            <i>microPublication Biology</i> takes allegations of scientific
            misconduct seriously and will investigate each on a case by case
            basis when brought to our attention, either by authors or concerned
            readers. We consult the guidelines provided by{" "}
            <Link href="https://publicationethics.org/" isExternal>
              COPE
            </Link>{" "}
            (Committee on Publication Ethics), International Committee of
            Medical Journal Editors (
            <Link href="http://www.icmje.org/recommendations/" isExternal>
              ICMJE
            </Link>
            ).
          </Paragraph>
          <Paragraph>
            <i>microPublication Biology</i> reserves the right to contact the
            authors’ institutions, funders, or regulatory bodies if needed.
            Should the allegations occur prior to publication of the article, we
            reserve the right to halt the peer review and publication process
            until the investigation is complete. In the case of published
            articles we will take the appropriate steps to correct the
            scientific record. These may include issuing an expression of
            concern or retraction of the published article.
          </Paragraph>
          <Paragraph>
            Please address any concerns or questions to our{" "}
            <Link href="mailto:editors@micropublication.org">
              editorial team (editors@micropublication.org)
            </Link>
            .
          </Paragraph>
        </>
      ),
    },
    {
      title: "Author Fees",
      text: (
        <Paragraph>
          As part of our long-term sustainability plan, we will institute
          article processing charges (APCs) starting April 2nd, 2022. We will
          charge $250 per article, which should cover costs but not new
          initiatives. No article will be refused because of an author’s
          inability to pay page charges. To request a waiver, please fill out{" "}
          <Link
            href="https://docs.google.com/forms/d/1Is0Dq3qYJNlg4fFVzdzHhQJby6qUUjis2gR-u_q7MOU/edit#responses"
            isExternal
          >
            this form
          </Link>
          . Contact{" "}
          <Link href="mailto:billing@micropublication.org">
            billing@micropublication.org
          </Link>{" "}
          for any further questions.
        </Paragraph>
      ),
    },
    {
      title: "Appeals",
      text: (
        <Paragraph>
          Authors or Associated Principal Investigators, in the case they were
          not included in authorship, who wish to appeal a decision on an
          article or make a complaint, should contact a member of the editorial
          team at once. The contacted editorial staff member will provide
          information about the journal’s complaints procedure and relay the
          issue to senior editorial staff members.
        </Paragraph>
      ),
    },
  ];

  return (
    <Section
      items={items}
      title="For Authors"
      sectionRef={authorsRef}
      isPage={isPage}
      hash={hash}
    />
  );
};

export default AuthorsSection;
